import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FiPlusCircle } from 'react-icons/fi';
import { Button, Dropdown, Input, Container } from '@/components';
import { useBts } from '@/context/btsContext/BtsContextProvider';
import { useUser } from '@/context/userContext/UserContextProvider';
import { useNavigate, Link } from 'react-router-dom';
import ProgressBarTabs from './ProgressBarTabs';
import ProposalProduct from './ProposalProduct';
import { ModalProvider } from '../../components/common/modal/ModalContext';



const UploadBtsProposal = () => {
  const {
    btsSchool,
    setBtsSchool,
    createBtsSchoolProposalInfo,
    updateBtsSchoolProposalInfo,
    error: btsError,
    setError,
  } = useBts();

  const { getSalesReps, salesReps } = useUser();
  const navigate = useNavigate();

  const steps = ['School Section', 'Products'];

  const [activeStep, setActiveStep] = useState(1);
  const formRef = useRef();

  const [logoPreview, setLogoPreview] = useState(
    btsSchool.proposal.schoolLogoUrl || '',
  );
  const [imagePath, setImagePath] = useState(btsSchool.proposal.logo || '');
  const fileInputRef = useRef(null);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    setLogoPreview(btsSchool.proposal.schoolLogoUrl || '');
    setImagePath(btsSchool.proposal.logo || '');
  }, [btsSchool.proposal.schoolLogoUrl, btsSchool.proposal.logo]);

  // Multi-email state
  const [emailInput, setEmailInput] = useState('');
  const [emailList, setEmailList] = useState(() => {
    if (btsSchool.proposal.emailList && btsSchool.proposal.emailList.length > 0) {
      return btsSchool.proposal.emailList;
    }
    if (btsSchool.proposal.email) {
      return btsSchool.proposal.email.split(',').map((e) => e.trim()).filter(Boolean);
    }
    return [];
  });

  const requiredFields = [
    'schoolName',
    'businessManager',
    'phoneNumber',
    'booklistType',
    'salesRep',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBtsSchool((prev) => ({
      ...prev,
      proposal: {
        ...prev.proposal,
        [name]: value,
      },
    }));
  };

  // Sync emailList into proposal state
  const syncEmailList = useCallback((list) => {
    setBtsSchool((prev) => ({
      ...prev,
      proposal: {
        ...prev.proposal,
        emailList: list,
        email: list.join(', '),
      },
    }));
  }, [setBtsSchool]);

  const addEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed) return;
    if (!isValidEmail(trimmed)) {
      setFieldErrors((prev) => ({ ...prev, emailList: 'Enter a valid email address.' }));
      return;
    }
    if (emailList.includes(trimmed)) {
      setFieldErrors((prev) => ({ ...prev, emailList: 'This email is already added.' }));
      return;
    }
    const updated = [...emailList, trimmed];
    setEmailList(updated);
    syncEmailList(updated);
    setEmailInput('');
    setFieldErrors((prev) => ({ ...prev, emailList: undefined }));
  };

  const removeEmail = async (index) => {
    const updated = emailList.filter((_, i) => i !== index);
    setEmailList(updated);
    syncEmailList(updated);

    if (proposalId) {
      try {
        const updatedProposal = {
          ...btsSchool.proposal,
          emailList: updated,
          email: updated.join(', '),
        };
        await updateBtsSchoolProposalInfo(updatedProposal, proposalId);
      } catch (err) {
        setEmailList(emailList);
        syncEmailList(emailList);
        setFieldErrors((prev) => ({ ...prev, emailList: 'Failed to remove email. Please try again.' }));
      }
    }
  };

  const handleEmailInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  const handleDropdownChange = (name, value) => {
    setBtsSchool((prev) => ({
      ...prev,
      proposal: {
        ...prev.proposal,
        [name]: value,
      },
    }));
  };

  const handleSalesRepSelect = (e) => {
    let selectedId;
    if (e && e.target) {
      selectedId = e.target.value;
    } else if (e && e.value !== undefined) {
      selectedId = e.value;
    }
    setBtsSchool((prev) => ({
      ...prev,
      proposal: {
        ...prev.proposal,
        salesRep: { id: selectedId ? parseInt(selectedId) : null },
      },
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/jpg',
      'image/avif',
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file && !allowedTypes.includes(file.type)) {
      setFormError(
        'Invalid file type. Only .jpg, .jpeg, .png, and .avif files are allowed.',
      );
      fileInputRef.current.value = null;
    } else if (file && file.size > maxSize) {
      setFormError('File size should not exceed 5MB');
      fileInputRef.current.value = null;
    } else {
      setFormError('');
      setFieldErrors((prev) => ({ ...prev, logo: undefined }));
      setBtsSchool((prev) => ({
        ...prev,
        proposal: {
          ...prev.proposal,
          logoFile: file,
          schoolLogoUrl: file ? '' : prev.proposal.schoolLogoUrl,
        },
      }));
      setLogoPreview(file ? URL.createObjectURL(file) : '');
      setImagePath(file ? file.name : btsSchool.proposal.logo || '');
    }
  };

  const handleFieldBlur = (e) => {
    const { name } = e.target;
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormError('');
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  const isValidPhone = (phone) => {
    return /^\d{10,}$/.test(phone.trim());
  };

  const validateForm = () => {
    let errors = {};
    for (let field of requiredFields) {
      let value = btsSchool.proposal[field];
      if (typeof value === 'string') value = value.trim();
      if (!value || value === '') {
        errors[field] = 'This field is required.';
      }
    }
    if (!emailList || emailList.length === 0) {
      errors.emailList = 'At least one email address is required.';
    }
    if (
      btsSchool.proposal.phoneNumber &&
      !isValidPhone(btsSchool.proposal.phoneNumber)
    ) {
      errors.phoneNumber = 'Enter a valid phone number (min 10 digits).';
    }
    if (
      !btsSchool.proposal.salesRep ||
      !btsSchool.proposal.salesRep.id ||
      btsSchool.proposal.salesRep.id === 0
    ) {
      errors.salesRep = 'Select a sales rep.';
    }

    // Logo validation
    const hasLogo = btsSchool.proposal.logoFile || btsSchool.proposal.schoolLogoUrl || btsSchool.proposal.logo;
    if (!hasLogo) {
      errors.logo = 'School logo is required.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const proposalId = btsSchool?.proposal?.id;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormError('');
    setFieldErrors({});
    setError && setError(null);

    try {
      let resp;
      if (proposalId) {
        resp = await updateBtsSchoolProposalInfo(btsSchool.proposal, proposalId);
      } else {
        resp = await createBtsSchoolProposalInfo(btsSchool);
      }

      if (resp && resp.id) {
        setBtsSchool((prev) => ({
          ...prev,
          proposal: {
            ...prev.proposal,
            ...resp,
          },
        }));
        goToNextSection();
      } else {
        setFormError('Failed to save proposal. Please try again.');
      }
    } catch (err) {
      setFormError(err?.message || 'An error occurred');
    }
  };

  const goToNextSection = () => {
    setActiveStep(2);
  };

  const handleStepClick = (stepIdx) => {
    if (activeStep === 1 && stepIdx === 1 && formRef.current) {
      formRef.current.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true }),
      );
    } else {
      setActiveStep(stepIdx + 1);
    }
  };

  useEffect(() => {
    getSalesReps();
    // eslint-disable-next-line
  }, []);

  const salesRepOptions = salesReps.map((rep) => ({
    value: rep.id,
    label: `${rep.salesRep} (${rep.firstName} ${rep.lastName})`,
    data: rep.id,
  }));

  useEffect(() => {
    if (!showPreview) return;
    const handleClickOutside = (event) => {
      if (previewRef.current && !previewRef.current.contains(event.target)) {
        setShowPreview(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPreview]);

  return (
    <Container title={proposalId ? "Edit BTS Proposal" : "Upload BTS Proposal"}>
      <div className="mb-6 ml-10">
        <Link
          to="/bts"
          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors duration-150 group">
          <svg
            className="w-5 h-5 mr-1 group-hover:-translate-x-1 transition-transform duration-150"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
            aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          <span>Back to BTS</span>
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="font-500 text-xl lg:text-2xl px-5 py-2 bg-gray-200">
          {activeStep === 1 ? 'School Information' : 'Products'}
        </div>

        {activeStep === 1 && (
          <form onSubmit={handleSave} ref={formRef}>
            <div className="p-5 grid grid-cols-2 gap-4">
              <Input
                label="School Name"
                name="schoolName"
                placeholder="Enter school name"
                className="mb-4"
                value={btsSchool.proposal.schoolName || ''}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                required
                error={fieldErrors.schoolName}
              />
              <Dropdown
                label="Sales Rep"
                name="salesRep"
                options={salesRepOptions}
                value={
                  btsSchool.proposal.salesRep?.id !== undefined &&
                    btsSchool.proposal.salesRep?.id !== null &&
                    btsSchool.proposal.salesRep?.id !== 0
                    ? String(btsSchool.proposal.salesRep.id)
                    : ''
                }
                placeholder="Select Sales Rep"
                className="mb-4"
                onChange={handleSalesRepSelect}
                onBlur={handleFieldBlur}
                required
                error={fieldErrors.salesRep}
              />
              <Input
                label="Business Manager"
                name="businessManager"
                value={btsSchool.proposal.businessManager || ''}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                placeholder="Enter business manager name"
                className="mb-4"
                required
                error={fieldErrors.businessManager}
              />
              <Input
                label="Phone Number"
                name="phoneNumber"
                value={btsSchool.proposal.phoneNumber || ''}
                onChange={handleInputChange}
                onBlur={handleFieldBlur}
                placeholder="Enter phone number"
                type="tel"
                className="mb-4"
                required
                error={fieldErrors.phoneNumber}
              />
              <Dropdown
                label="BookList Type"
                name="booklistType"
                value={btsSchool.proposal.booklistType || ''}
                onChange={(e) =>
                  handleDropdownChange('booklistType', e.value || e.target.value)
                }
                onBlur={handleFieldBlur}
                placeholder="Select Booklist Type"
                options={[
                  { value: 'Booklist', label: 'Booklist' },
                  { value: 'Bulk', label: 'Bulk' },
                ]}
                required
                error={fieldErrors.booklistType}
                disabled={!!proposalId}
              />
              <div onKeyDown={handleEmailInputKeyDown}>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Input
                      label="Email (First email will be the primary email)"
                      name="emailInput"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="Enter contact email"
                      type="text"
                      error={fieldErrors.emailList}
                    />
                  </div>
                  <div className="pb-2">
                    <button
                      type="button"
                      onClick={addEmail}
                      className="flex items-center justify-center text-gray-400 hover:text-gray-600"
                      title="Add email"
                    >
                      <FiPlusCircle size={36} strokeWidth={1.2} />
                    </button>
                  </div>
                </div>
                {emailList.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-2 mt-1 mb-2">
                    {emailList.map((email, index) => (
                      <span
                        key={index}
                        className="flex items-center gap-1 bg-gray-100 border border-gray-300 text-gray-700 text-sm rounded-full px-3 py-1"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => removeEmail(index)}
                          className="ml-1 text-gray-400 hover:text-red-500 font-bold leading-none"
                          title="Remove"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <Input
                label="Created Date"
                name="createdDate"
                value={
                  btsSchool.proposal.createdDate
                    ? new Date(btsSchool.proposal.createdDate).toLocaleDateString()
                    : new Date().toLocaleDateString()
                }
                required
                placeholder="Enter created date"
                type="text"
                disabled
              />
              <div>
                <Input
                  type="file"
                  label="Logo"
                  name="logo"
                  placeholder="Enter logo"
                  className="mb-4"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  required={!proposalId}
                  error={fieldErrors.logo}
                />
                <div className="mt-2">
                  {(logoPreview || btsSchool.proposal.schoolLogoUrl) && (
                    <div className="flex items-center gap-2">
                      <span
                        className="text-blue-500 cursor-pointer text-sm font-medium"
                        onClick={() => setShowPreview(true)}>
                        Preview Image
                      </span>
                      {!logoPreview && btsSchool.proposal.schoolLogoUrl && (
                        <a
                          href={btsSchool.proposal.schoolLogoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline text-xs">
                          View Logo Link
                        </a>
                      )}
                    </div>
                  )}
                  {showPreview && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                      <div
                        ref={previewRef}
                        className="p-4 rounded-lg bg-white max-w-lg shadow-xl">
                        <img
                          src={logoPreview || btsSchool.proposal.schoolLogoUrl}
                          alt="School Logo"
                          className="max-h-[70vh] max-w-full object-contain"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {formError && (
                <div className="col-span-2 text-red-500 text-sm ml-2 mt-2">
                  {formError}
                </div>
              )}

              {btsError && (
                <div className="col-span-2 text-red-500 text-sm ml-2 mt-2">
                  {btsError}
                </div>
              )}

              <div className="col-span-2 flex justify-end gap-2 mt-4 border-t pt-4">
                <Button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-3xl">
                  Next
                </Button>
              </div>
            </div>
          </form>
        )}

        {activeStep === 2 && (
          <ModalProvider>
            <ProposalProduct
              proposalId={proposalId}
              goToNextSection={goToNextSection}
              isUploadWizard={true}
            />
          </ModalProvider>
        )}
      </div>

      <ProgressBarTabs
        steps={steps}
        activeStep={activeStep}
        onStepClick={handleStepClick}
      />
    </Container>
  );
};

export default UploadBtsProposal;
